import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { Camera, ScanLine, X } from "lucide-react";

const CATEGORY_OPTIONS = [
  { value: "Telefona", label: "Telefona" },
  { value: "Laptop", label: "Laptop" },
  { value: "Tablet", label: "Tablet" },
  { value: "Kufje", label: "Kufje" },
  { value: "Aksesore", label: "Aksesorë" },
  { value: "Other", label: "Tjetër" },
];

const CONDITION_OPTIONS = [
  { value: "Te Reja", label: "Të reja" },
  { value: "Te Perdorura", label: "Të përdorura" },
];

const DEFAULT_BRANDS = {
  Telefona: [
    "Apple",
    "Samsung",
    "Xiaomi",
    "Huawei",
    "Honor",
    "OnePlus",
    "Oppo",
    "Realme",
    "Google",
    "Motorola",
    "Nokia",
  ],

  Laptop: [
    "Apple",
    "Lenovo",
    "HP",
    "Dell",
    "Asus",
    "Acer",
    "Microsoft",
    "MSI",
  ],

  Tablet: [
    "Apple",
    "Samsung",
    "Lenovo",
    "Huawei",
    "Xiaomi",
    "Honor",
    "Microsoft",
  ],

  Kufje: [
    "Apple",
    "Samsung",
    "JBL",
    "Sony",
    "Trust",
    "Xiaomi",
    "Huawei",
    "Anker",
    "Baseus",
  ],

  Aksesore: [
    "Apple",
    "Samsung",
    "Xiaomi",
    "Huawei",
    "Baseus",
    "Anker",
    "Trust",
    "JBL",
    "Logitech",
  ],

  Other: [],
};

const MEMORY_OPTIONS = [
  "32 GB",
  "64 GB",
  "128 GB",
  "256 GB",
  "512 GB",
  "1 TB",
  "2 TB",
];

const RAM_OPTIONS = [
  "4 GB",
  "6 GB",
  "8 GB",
  "12 GB",
  "16 GB",
  "24 GB",
  "32 GB",
  "64 GB",
];

const BATTERY_OPTIONS = [
  "3000 mAh",
  "4000 mAh",
  "4500 mAh",
  "5000 mAh",
  "6000 mAh",
];

const COLOR_OPTIONS = [
  "Black",
  "White",
  "Blue",
  "Red",
  "Green",
  "Gray",
  "Silver",
  "Gold",
  "Purple",
  "Pink",
];

const initialFormState = {
  product_condition: "",
  category: "",
  brand: "",
  customBrand: "",
  model: "",
  customModel: "",
  name: "",
  purchase_price: "",
  price: "",
  stock: "",
  memory: "",
  ram: "",
  battery: "",
  color: "",
  imei: "",
  barcode: "",
  description: "",
  is_published: "0",
  is_offer: "0",
  discount: "0",
  max_discount_percent: "0",
  bonus_share_percent: "0",
  images: null,
};

const generateProductBarcode = () => {
  const now = new Date();

  const datePart = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("");

  const randomPart =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID().replaceAll("-", "").slice(0, 8).toUpperCase()
      : Math.random().toString(36).slice(2, 10).toUpperCase();

  return `PRD-${datePart}-${randomPart}`;
};

const normalizeCategory = (category = "") =>
  category
    .trim()
    .toLowerCase()
    .replaceAll("ë", "e");

const hasTechnicalSpecifications = (category) => {
  const normalized = normalizeCategory(category);

  return [
    "telefona",
    "telefon",
    "laptop",
    "tablet",
  ].includes(normalized);
};

const AddProductForm = forwardRef(
  (
    {
      onAdd,
      initialData,
      products = [],
    },
    ref
  ) => {
    const fileInputRef = useRef(null);
    const cameraVideoRef = useRef(null);
    const scannerControlsRef = useRef(null);

    const [formData, setFormData] =
      useState(initialFormState);

    const [imagePreview, setImagePreview] =
      useState("");

    const [submitting, setSubmitting] =
      useState(false);

    const [error, setError] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});
    const [scannerOpen, setScannerOpen] = useState(false);
    const [scannerBusy, setScannerBusy] = useState(false);
    const [scannerError, setScannerError] = useState("");

    const showTechnicalFields =
      hasTechnicalSpecifications(
        formData.category
      );

    const resetForm = () => {
      setFormData(initialFormState);
      setImagePreview("");
      setError("");
      setFieldErrors({});

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    useImperativeHandle(ref, () => ({
      resetForm,
    }));

    /* =========================
       PREFILL FOR EDIT
    ========================= */

    useEffect(() => {
      if (!initialData) {
        resetForm();
        return;
      }

      const initialBrand =
        initialData.brand || "";

      const initialModel =
        initialData.model || "";

      setFormData({
        product_condition:
          initialData.product_condition || "",

        category:
          initialData.category || "",

        brand: initialBrand,
        customBrand: "",

        model: initialModel,
        customModel: "",

        name: initialData.name || "",

        purchase_price:
          initialData.purchase_price ?? "",

        price:
          initialData.price ?? "",

        stock:
          initialData.stock ?? "",

        memory:
          initialData.memory || "",

        ram:
          initialData.ram || "",

        battery:
          initialData.battery || "",

        color:
          initialData.color || "",

        imei:
          initialData.imei || "",

        barcode:
          initialData.barcode || "",

        description:
          initialData.description || "",

        is_published: String(
          Number(
            initialData.is_published || 0
          )
        ),

        is_offer: String(
          Number(
            initialData.is_offer || 0
          )
        ),

        discount: String(
          Number(
            initialData.discount || 0
          )
        ),

        max_discount_percent: String(
          Number(
            initialData.max_discount_percent || 0
          )
        ),

        bonus_share_percent: String(
          Number(
            initialData.bonus_share_percent || 0
          )
        ),

        images: null,
      });

      if (initialData.images) {
        setImagePreview(
          initialData.images.startsWith("http")
            ? initialData.images
            : `${
                process.env.REACT_APP_API_URL ||
                "https://masterphone-backend-2.onrender.com"
              }${initialData.images}`
        );
      }
    }, [initialData]);

    /* =========================
       BRAND OPTIONS
    ========================= */

    const brandOptions = useMemo(() => {
      const values = new Set(
        DEFAULT_BRANDS[
          formData.category
        ] || []
      );

      products.forEach((product) => {
        if (
          product.category ===
            formData.category &&
          product.brand
        ) {
          values.add(product.brand);
        }
      });

      return Array.from(values).sort();
    }, [
      products,
      formData.category,
    ]);

    /* =========================
       MODEL OPTIONS
    ========================= */

    const selectedBrand =
      formData.brand === "__custom__"
        ? formData.customBrand.trim()
        : formData.brand;

    const selectedModel =
      formData.model === "__custom__"
        ? formData.customModel.trim()
        : formData.model;

    const modelOptions = useMemo(() => {
      const values = new Set();

      products.forEach((product) => {
        const sameCategory =
          product.category ===
          formData.category;

        const sameBrand =
          product.brand === selectedBrand;

        if (
          sameCategory &&
          sameBrand &&
          product.model
        ) {
          values.add(product.model);
        }
      });

      return Array.from(values).sort();
    }, [
      products,
      formData.category,
      selectedBrand,
    ]);

    /* =========================
       AUTO PRODUCT NAME
    ========================= */

    useEffect(() => {
      const generatedName = [
        selectedBrand,
        selectedModel,
      ]
        .filter(Boolean)
        .join(" ")
        .trim();

      setFormData((previous) => ({
        ...previous,
        name: generatedName,
      }));
    }, [
      selectedBrand,
      selectedModel,
    ]);

    /* =========================
       RESET TECHNICAL FIELDS
    ========================= */

    useEffect(() => {
      if (showTechnicalFields) {
        return;
      }

      setFormData((previous) => ({
        ...previous,
        memory: "",
        ram: "",
        battery: "",
        color: "",
      }));
    }, [showTechnicalFields]);

    const handleChange = (event) => {
      const {
        name,
        value,
        files,
      } = event.target;

      if (name === "images") {
        const file = files?.[0] || null;

        setFormData((previous) => ({
          ...previous,
          images: file,
        }));

        if (file) {
          setImagePreview(
            URL.createObjectURL(file)
          );
        }
        setFieldErrors((previous) => {
          const next = { ...previous };
          delete next.images;
          return next;
        });
        if (error) setError("");
        return;
      }

      setFormData((previous) => {
        if (name === "is_offer") {
          return {
            ...previous,
            is_offer: value,
            discount:
              value === "1"
                ? previous.discount
                : "0",
          };
        }

        return {
          ...previous,
          [name]: value,
        };
      });

      setFieldErrors((previous) => {
        const next = { ...previous };
        delete next[name];
        if (name === "customBrand" || name === "brand") delete next.brand;
        if (name === "customModel" || name === "model") delete next.model;
        return next;
      });
      if (error) setError("");
    };

    const stopBarcodeScanner = () => {
      try {
        scannerControlsRef.current?.stop();
      } catch (error) {
        console.warn("PRODUCT BARCODE SCANNER STOP ERROR:", error);
      }

      scannerControlsRef.current = null;

      const video = cameraVideoRef.current;
      const stream = video?.srcObject;

      if (stream?.getTracks) {
        stream.getTracks().forEach((track) => track.stop());
      }

      if (video) {
        video.srcObject = null;
      }

      setScannerBusy(false);
    };

    const closeBarcodeScanner = () => {
      stopBarcodeScanner();
      setScannerOpen(false);
      setScannerError("");
    };

    const startBarcodeScanner = async () => {
      stopBarcodeScanner();
      setScannerError("");

      if (!navigator.mediaDevices?.getUserMedia) {
        setScannerError(
          "Kamera nuk është e disponueshme. Hape faqen me HTTPS dhe lejo aksesin te kamera."
        );
        setScannerOpen(true);
        return;
      }

      setScannerOpen(true);
      setScannerBusy(true);

      try {
        await new Promise((resolve) => window.setTimeout(resolve, 200));

        const videoElement = cameraVideoRef.current;

        if (!videoElement) {
          throw new Error("Elementi video i kamerës nuk u gjet.");
        }

        const codeReader = new BrowserMultiFormatReader();
        let handled = false;

        const controls = await codeReader.decodeFromConstraints(
          {
            video: {
              facingMode: { ideal: "environment" },
              width: { ideal: 1920 },
              height: { ideal: 1080 },
              frameRate: { ideal: 30, max: 60 },
            },
            audio: false,
          },
          videoElement,
          (result, error) => {
            if (handled) return;

            if (result) {
              handled = true;

              const value = String(result.getText?.() || result)
                .trim();

              setFormData((previous) => ({
                ...previous,
                barcode: value,
              }));

              setFieldErrors((previous) => {
                const next = { ...previous };
                delete next.barcode;
                return next;
              });

              closeBarcodeScanner();
              return;
            }

            if (
              error?.name &&
              error.name !== "NotFoundException"
            ) {
              console.debug(
                "PRODUCT BARCODE CAMERA SCAN:",
                error.name
              );
            }
          }
        );

        scannerControlsRef.current = controls;

        try {
          const stream = videoElement.srcObject;
          const track = stream?.getVideoTracks?.()[0];
          const capabilities = track?.getCapabilities?.() || {};
          const advanced = [];

          if (
            Array.isArray(capabilities.focusMode) &&
            capabilities.focusMode.includes("continuous")
          ) {
            advanced.push({ focusMode: "continuous" });
          }

          if (capabilities.zoom) {
            const minZoom = Number(capabilities.zoom.min ?? 1);
            const maxZoom = Number(capabilities.zoom.max ?? 1);
            const targetZoom = Math.min(
              maxZoom,
              Math.max(minZoom, 1.2)
            );
            advanced.push({ zoom: targetZoom });
          }

          if (advanced.length) {
            await track.applyConstraints({ advanced });
          }
        } catch (constraintError) {
          console.debug(
            "PRODUCT CAMERA ADVANCED CONSTRAINTS:",
            constraintError
          );
        }
      } catch (error) {
        console.error("PRODUCT BARCODE SCANNER ERROR:", error);
        stopBarcodeScanner();

        let message =
          error?.message ||
          "Kamera nuk mund të hapej për skanim.";

        if (error?.name === "NotAllowedError") {
          message =
            "Aksesi te kamera u refuzua. Lejo kamerën nga browser-i dhe provo përsëri.";
        } else if (error?.name === "NotFoundError") {
          message =
            "Nuk u gjet asnjë kamerë në këtë pajisje.";
        } else if (error?.name === "NotReadableError") {
          message =
            "Kamera është duke u përdorur nga një aplikacion tjetër.";
        }

        setScannerError(message);
      }
    };

    useEffect(() => {
      return () => {
        stopBarcodeScanner();
      };
    }, []);

    const validateForm = () => {
      const errors = {};

      if (!formData.category) errors.category = "Zgjidh kategorinë.";
      if (!formData.product_condition) errors.product_condition = "Zgjidh gjendjen e produktit.";
      if (!selectedBrand) errors.brand = "Zgjidh ose shkruaj brand-in.";
      if (!selectedModel) errors.model = "Zgjidh ose shkruaj modelin.";
      if (!formData.imei.trim()) errors.imei = "IMEI është i detyrueshëm.";

      const purchasePrice = Number(formData.purchase_price);
      if (formData.purchase_price === "" || !Number.isFinite(purchasePrice) || purchasePrice < 0)
        errors.purchase_price = "Vendos një çmim hyrjeje të vlefshëm.";

      const price = Number(formData.price);
      if (formData.price === "" || !Number.isFinite(price) || price < 0)
        errors.price = "Vendos një çmim shitjeje të vlefshëm.";

      const stock = Number(formData.stock);
      if (formData.stock === "" || !Number.isInteger(stock) || stock < 0)
        errors.stock = "Stoku duhet të jetë numër i plotë pozitiv.";

      const discount = Number(formData.discount);
      if (formData.is_offer === "1" &&
          (!Number.isFinite(discount) || discount < 0 || discount > 100))
        errors.discount = "Ulja duhet të jetë nga 0 deri në 100%.";

      const maxDiscount = Number(formData.max_discount_percent);
      if (!Number.isFinite(maxDiscount) || maxDiscount < 0 || maxDiscount > 100)
        errors.max_discount_percent = "Ulja maksimale duhet të jetë nga 0 deri në 100%.";

      const bonus = Number(formData.bonus_share_percent);
      if (!Number.isFinite(bonus) || bonus < 0 || bonus > 100)
        errors.bonus_share_percent = "Bonusi duhet të jetë nga 0 deri në 100%.";

      setFieldErrors(errors);
      return Object.keys(errors).length
        ? "Ka fusha të paplotësuara ose të pavlefshme. Kontrollo fushat me të kuqe."
        : "";
    };

    const handleSubmit = async (
      event
    ) => {
      event.preventDefault();

      const validationError =
        validateForm();

      if (validationError) {
        setError(validationError);
        return;
      }

      setError("");
      setSubmitting(true);

      const payload = new FormData();

      payload.append(
        "product_condition",
        formData.product_condition
      );

      payload.append(
        "category",
        formData.category
      );

      payload.append(
        "brand",
        selectedBrand
      );

      payload.append(
        "model",
        selectedModel
      );

      payload.append(
        "name",
        formData.name.trim()
      );

      payload.append(
        "purchase_price",
        String(
          Number(formData.purchase_price || 0)
        )
      );

      payload.append(
        "price",
        String(
          Number(formData.price || 0)
        )
      );

      payload.append(
        "stock",
        String(
          Number(formData.stock || 0)
        )
      );

      payload.append(
        "description",
        formData.description.trim()
      );

      payload.append(
        "imei",
        formData.imei.trim()
      );

      const scannedBarcode = formData.barcode.trim();

      // Nëse produkti është skanuar, ruaj barcode-in e skanuar.
      // Nëse nuk është skanuar / fusha është bosh, gjenero barcode automatik.
      const finalBarcode =
        scannedBarcode || generateProductBarcode();

      payload.append(
        "barcode",
        finalBarcode
      );

      // Shfaq kodin e gjeneruar në formular para se të mbyllet.
      if (!scannedBarcode) {
        setFormData((previous) => ({
          ...previous,
          barcode: finalBarcode,
        }));
      }

      payload.append(
        "is_published",
        formData.is_published
      );

      payload.append(
        "is_offer",
        formData.is_offer
      );

      payload.append(
        "discount",
        formData.is_offer === "1"
          ? String(
              Number(
                formData.discount || 0
              )
            )
          : "0"
      );

      payload.append(
        "max_discount_percent",
        String(
          Number(
            formData.max_discount_percent || 0
          )
        )
      );

      payload.append(
        "bonus_share_percent",
        String(
          Number(
            formData.bonus_share_percent || 0
          )
        )
      );

      /*
       * Vetëm produktet teknike
       * dërgojnë këto fusha.
       */
      payload.append(
        "memory",
        showTechnicalFields
          ? formData.memory
          : ""
      );

      payload.append(
        "ram",
        showTechnicalFields
          ? formData.ram
          : ""
      );

      payload.append(
        "battery",
        showTechnicalFields
          ? formData.battery
          : ""
      );

      payload.append(
        "color",
        showTechnicalFields
          ? formData.color
          : ""
      );

      if (
        formData.images instanceof File
      ) {
        payload.append(
          "images",
          formData.images
        );
      }

      try {
        await onAdd?.(payload);
      } catch (submitError) {
        setError(
          submitError.message ||
            "Produkti nuk mund të ruhej."
        );
      } finally {
        setSubmitting(false);
      }
    };

    const errorStyle = (field) =>
      fieldErrors[field]
        ? {
            border: "2px solid #dc2626",
            backgroundColor: "#fff1f2",
            boxShadow: "0 0 0 3px rgba(220, 38, 38, 0.10)",
          }
        : undefined;

    const FieldError = ({ field }) =>
      fieldErrors[field] ? (
        <p className="mt-1 text-xs font-semibold text-red-600">
          {fieldErrors[field]}
        </p>
      ) : null;

    return (
      <div className="rounded-2xl bg-white">
        <form
          onSubmit={handleSubmit}
          noValidate
          className="space-y-5 p-5"
        >
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
              {(() => {
                const msg = String(error || "");
                if (/required|is required|please fill|cannot be empty/i.test(msg))
                  return "Plotëso të gjitha fushat e detyrueshme.";
                if (/invalid/i.test(msg))
                  return "Ka të dhëna të pavlefshme. Kontrollo fushat.";
                if (/failed to fetch|network error/i.test(msg))
                  return "Nuk u arrit lidhja me serverin. Provo përsëri.";
                if (/product.*exist|already exists|duplicate/i.test(msg))
                  return "Ky produkt ekziston tashmë.";
                if (/failed|could not|cannot save|couldn't save/i.test(msg))
                  return "Produkti nuk mund të ruhej. Kontrollo të dhënat dhe provo përsëri.";
                return msg;
              })()}
            </div>
          )}

          {/* CATEGORY + CONDITION */}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                Kategoria *
              </label>

              <select
                name="category"
                value={formData.category}
                onChange={(event) => {
                  handleChange(event);

                  setFormData(
                    (previous) => ({
                      ...previous,
                      category:
                        event.target.value,
                      brand: "",
                      customBrand: "",
                      model: "",
                      customModel: "",
                      name: "",
                    })
                  );
                }}
                style={errorStyle("category")}
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-3 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              >
                <option value="">
                  Zgjidh kategorinë
                </option>

                {CATEGORY_OPTIONS.map(
                  (option) => (
                    <option
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  )
                )}
              </select>
              <FieldError field="category" />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                Gjendja *
              </label>

              <select
                name="product_condition"
                value={
                  formData.product_condition
                }
                onChange={handleChange}
                style={errorStyle("product_condition")}
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-3 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              >
                <option value="">
                  Zgjidh gjendjen
                </option>

                {CONDITION_OPTIONS.map(
                  (option) => (
                    <option
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  )
                )}
              </select>
              <FieldError field="product_condition" />
            </div>
          </div>

          {/* BRAND + MODEL */}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                Brand *
              </label>

              <select
                name="brand"
                value={formData.brand}
                onChange={(event) => {
                  handleChange(event);

                  setFormData(
                    (previous) => ({
                      ...previous,
                      brand:
                        event.target.value,
                      model: "",
                      customModel: "",
                    })
                  );
                }}
                disabled={
                  !formData.category
                }
                style={errorStyle("brand")}
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-3 outline-none disabled:bg-gray-100 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              >
                <option value="">
                  Zgjidh brand-in
                </option>

                {brandOptions.map(
                  (brand) => (
                    <option
                      key={brand}
                      value={brand}
                    >
                      {brand}
                    </option>
                  )
                )}

                <option value="__custom__">
                  + Brand tjetër
                </option>
              </select>
              <FieldError field="brand" />

              {formData.brand ===
                "__custom__" && (
                <input
                  type="text"
                  name="customBrand"
                  value={
                    formData.customBrand
                  }
                  onChange={handleChange}
                  placeholder="Shkruaj brand-in"
                  style={errorStyle("brand")}
                  className="mt-2 w-full rounded-xl border border-gray-300 px-3 py-3 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                />
              )}
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                Modeli *
              </label>

              <select
                name="model"
                value={formData.model}
                onChange={handleChange}
                disabled={!selectedBrand}
                style={errorStyle("model")}
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-3 outline-none disabled:bg-gray-100 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              >
                <option value="">
                  Zgjidh modelin
                </option>

                {modelOptions.map(
                  (model) => (
                    <option
                      key={model}
                      value={model}
                    >
                      {model}
                    </option>
                  )
                )}

                <option value="__custom__">
                  + Model i ri
                </option>
              </select>
              <FieldError field="model" />

              {formData.model ===
                "__custom__" && (
                <input
                  type="text"
                  name="customModel"
                  value={
                    formData.customModel
                  }
                  onChange={handleChange}
                  placeholder="Shkruaj modelin"
                  style={errorStyle("model")}
                  className="mt-2 w-full rounded-xl border border-gray-300 px-3 py-3 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                />
              )}
            </div>
          </div>

          {/* AUTO GENERATED NAME */}

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-gray-700">
              Emri i produktit
            </label>

            <input
              type="text"
              value={formData.name}
              readOnly
              className="w-full rounded-xl border border-blue-200 bg-blue-50 px-3 py-3 font-semibold text-blue-800"
              placeholder="Do të krijohet automatikisht"
            />

            <p className="mt-1 text-xs text-gray-500">
              Emri krijohet automatikisht
              nga brand-i dhe modeli.
            </p>
          </div>

          {/* PURCHASE PRICE + SALE PRICE + STOCK */}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                Çmimi i hyrjes *
              </label>

              <div className="relative">
                <input
                  name="purchase_price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.purchase_price}
                  onChange={handleChange}
                  style={errorStyle("purchase_price")}
                className="w-full rounded-xl border border-gray-300 px-3 py-3 pr-14 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                />
              <FieldError field="purchase_price" />

                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-500">
                  ALL
                </span>
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                Çmimi i shitjes *
              </label>

              <div className="relative">
                <input
                  name="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={handleChange}
                  style={errorStyle("price")}
                className="w-full rounded-xl border border-gray-300 px-3 py-3 pr-14 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                />
              <FieldError field="price" />

                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-500">
                  ALL
                </span>
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                Stoku *
              </label>

              <input
                name="stock"
                type="number"
                min="0"
                step="1"
                value={formData.stock}
                onChange={handleChange}
                style={errorStyle("stock")}
                className="w-full rounded-xl border border-gray-300 px-3 py-3 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              />
              <FieldError field="stock" />
            </div>
          </div>

          {/* BARCODE */}

          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <label className="mb-1.5 block text-sm font-semibold text-gray-700">
              Barcode
            </label>

            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                name="barcode"
                type="text"
                value={formData.barcode}
                onChange={handleChange}
                placeholder="Skano, shkruaj ose lëre bosh"
                autoComplete="off"
                className="min-w-0 flex-1 rounded-xl border border-gray-300 bg-white px-3 py-3 font-mono outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              />

              <button
                type="button"
                onClick={startBarcodeScanner}
                className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 font-semibold text-white transition hover:bg-blue-700"
              >
                <Camera size={18} />
                Skano
              </button>
            </div>

            <p className="mt-1 text-xs text-gray-500">
              Skano barcode-in me kamerë ose scanner USB/Bluetooth. Nëse produkti nuk ka barcode, lëre bosh dhe sistemi do të gjenerojë automatikisht një barcode unik kur ruhet.
            </p>
          </div>

          {/* TECHNICAL FIELDS */}

          {showTechnicalFields && (
            <div className="rounded-2xl border border-indigo-100 bg-indigo-50/50 p-4">
              <div className="mb-4">
                <h3 className="font-bold text-gray-900">
                  Specifikimet teknike
                </h3>

                <p className="mt-1 text-xs text-gray-500">
                  Këto fusha shfaqen vetëm
                  për telefona, laptopë dhe
                  tabletë.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                    Memoria
                  </label>

                  <select
                    name="memory"
                    value={
                      formData.memory
                    }
                    onChange={handleChange}
                    className="w-full rounded-xl border border-gray-300 bg-white px-3 py-3"
                  >
                    <option value="">
                      Zgjidh memorien
                    </option>

                    {MEMORY_OPTIONS.map(
                      (item) => (
                        <option
                          key={item}
                          value={item}
                        >
                          {item}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                    RAM
                  </label>

                  <select
                    name="ram"
                    value={formData.ram}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-gray-300 bg-white px-3 py-3"
                  >
                    <option value="">
                      Zgjidh RAM
                    </option>

                    {RAM_OPTIONS.map(
                      (item) => (
                        <option
                          key={item}
                          value={item}
                        >
                          {item}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                    Bateria
                  </label>

                  <select
                    name="battery"
                    value={
                      formData.battery
                    }
                    onChange={handleChange}
                    className="w-full rounded-xl border border-gray-300 bg-white px-3 py-3"
                  >
                    <option value="">
                      Zgjidh baterinë
                    </option>

                    {BATTERY_OPTIONS.map(
                      (item) => (
                        <option
                          key={item}
                          value={item}
                        >
                          {item}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                    Ngjyra
                  </label>

                  <select
                    name="color"
                    value={
                      formData.color
                    }
                    onChange={handleChange}
                    className="w-full rounded-xl border border-gray-300 bg-white px-3 py-3"
                  >
                    <option value="">
                      Zgjidh ngjyrën
                    </option>

                    {COLOR_OPTIONS.map(
                      (item) => (
                        <option
                          key={item}
                          value={item}
                        >
                          {item}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* IMEI + PUBLISH */}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                Imei*
              </label>

              <input
                name="imei"
                value={formData.imei}
                onChange={handleChange}
                style={errorStyle("imei")}
                className="w-full rounded-xl border border-gray-300 px-3 py-3"
                placeholder="Vendos Imei"
              />
              <FieldError field="imei" />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                Publikimi
              </label>

              <select
                name="is_published"
                value={
                  formData.is_published
                }
                onChange={handleChange}
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-3"
              >
                <option value="0">
                  Jo i publikuar
                </option>

                <option value="1">
                  Publiko në website
                </option>
              </select>
            </div>
          </div>

          {/* SALES RULES / INTERNAL BONUS */}

          <div className="rounded-2xl border border-violet-100 bg-violet-50/50 p-4">
            <div className="mb-4">
              <h3 className="font-bold text-gray-900">
                Rregullat e shitjes
              </h3>

              <p className="mt-1 text-xs text-gray-500">
                Bonusi llogaritet automatikisht nga fitimi i produktit:
                (Çmimi i shitjes - Çmimi i hyrjes) × Bonus %.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                  Ulje max shitësi %
                </label>

                <div className="relative">
                  <input
                    type="number"
                    name="max_discount_percent"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.max_discount_percent}
                    onChange={handleChange}
                    style={errorStyle("max_discount_percent")}
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-3 pr-10 outline-none focus:border-violet-500 focus:ring-4 focus:ring-violet-100"
                    placeholder="p.sh. 10"
                  />
              <FieldError field="max_discount_percent" />

                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-500">
                    %
                  </span>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                  Bonus nga fitimi %
                </label>

                <div className="relative">
                  <input
                    type="number"
                    name="bonus_share_percent"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.bonus_share_percent}
                    onChange={handleChange}
                    style={errorStyle("bonus_share_percent")}
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-3 pr-10 outline-none focus:border-violet-500 focus:ring-4 focus:ring-violet-100"
                    placeholder="p.sh. 10"
                  />
              <FieldError field="bonus_share_percent" />

                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-500">
                    %
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-3 rounded-xl bg-white/80 px-3 py-2 text-xs text-gray-600">
              Fitimi = Çmimi i shitjes - Çmimi i hyrjes. Bonusi = Fitimi × Bonus %.
            </div>
          </div>

          {/* OFFER + DISCOUNT */}

          <div className="rounded-2xl border border-orange-100 bg-orange-50/50 p-4">
            <div className="mb-4">
              <h3 className="font-bold text-gray-900">
                Oferta
              </h3>

              <p className="mt-1 text-xs text-gray-500">
                Aktivizo ofertën vetëm për produktet që do të shfaqen si ofertë në faqen kryesore.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                  Ofertë
                </label>

                <select
                  name="is_offer"
                  value={
                    formData.is_offer
                  }
                  onChange={handleChange}
                  className="w-full rounded-xl border border-gray-300 bg-white px-3 py-3 outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-100"
                >
                  <option value="0">
                    JO
                  </option>

                  <option value="1">
                    PO
                  </option>
                </select>
              </div>

              {formData.is_offer === "1" && (
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                    Ulje %
                  </label>

                  <div className="relative">
                    <input
                      type="number"
                      name="discount"
                      min="0"
                      max="100"
                      step="0.01"
                      value={
                        formData.discount
                      }
                      onChange={handleChange}
                      style={errorStyle("discount")}
                className="w-full rounded-xl border border-gray-300 px-3 py-3 pr-10 outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-100"
                      placeholder="p.sh. 20"
                    />
              <FieldError field="discount" />

                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-500">
                      %
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* IMAGE */}

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-gray-700">
              Fotografia
            </label>

            <input
              ref={fileInputRef}
              type="file"
              name="images"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleChange}
              className="w-full rounded-xl border border-dashed border-gray-300 bg-gray-50 p-3"
            />

            {imagePreview && (
              <div className="mt-3 overflow-hidden rounded-xl border border-gray-200 bg-gray-50 p-3">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="mx-auto h-40 max-w-full rounded-lg object-contain"
                />
              </div>
            )}
          </div>

          {/* DESCRIPTION */}

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-gray-700">
              Përshkrimi
            </label>

            <textarea
              name="description"
              rows="4"
              value={
                formData.description
              }
              onChange={handleChange}
              className="w-full resize-y rounded-xl border border-gray-300 px-3 py-3 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              placeholder="Përshkrimi i produktit..."
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="
              w-full rounded-xl
              bg-gradient-to-r
              from-blue-600
              to-indigo-700
              px-5 py-3
              font-bold text-white
              shadow-md transition
              hover:shadow-lg
              active:scale-[0.99]
              disabled:cursor-not-allowed
              disabled:opacity-60
            "
          >
            {submitting
              ? "Duke ruajtur..."
              : initialData
                ? "Ruaj ndryshimet"
                : "Shto produktin"}
          </button>
        </form>

        {scannerOpen && (
          <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
            <div className="w-full max-w-xl overflow-hidden rounded-3xl bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                <div>
                  <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                    <ScanLine size={20} className="text-blue-600" />
                    Skano barcode-in
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Drejto kamerën te barcode-i i produktit.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={closeBarcodeScanner}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200"
                  aria-label="Mbyll scanner-in"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-4">
                <div className="overflow-hidden rounded-2xl bg-black">
                  <video
                    ref={cameraVideoRef}
                    autoPlay
                    muted
                    playsInline
                    className="aspect-video w-full object-cover"
                  />
                </div>

                {scannerBusy && !scannerError && (
                  <p className="mt-3 text-center text-sm font-medium text-gray-600">
                    Duke skanuar...
                  </p>
                )}

                {scannerError && (
                  <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                    {scannerError}
                  </div>
                )}

                <button
                  type="button"
                  onClick={closeBarcodeScanner}
                  className="mt-4 w-full rounded-xl border border-gray-300 px-4 py-3 font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Mbyll
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default AddProductForm;